import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '../../lib/utils';

function TooltipProviderComponent({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

const TooltipProvider = React.memo(TooltipProviderComponent);

interface TooltipProps extends React.ComponentProps<typeof TooltipPrimitive.Root> {
  delayDuration?: number;
  providerProps?: Omit<React.ComponentProps<typeof TooltipPrimitive.Provider>, 'children' | 'delayDuration'>;
}

function TooltipComponent({
  delayDuration = 0,
  providerProps,
  ...props
}: TooltipProps) {
  return (
    <TooltipProvider delayDuration={delayDuration} {...providerProps}>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

const Tooltip = React.memo(TooltipComponent);

function TooltipTriggerComponent({
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

const TooltipTrigger = React.memo(TooltipTriggerComponent);

function TooltipContentComponent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          `tw-bg-foreground tw-text-background tw-animate-in tw-fade-in-0 tw-zoom-in-95 tw-data-[state=closed]:tw-animate-out
          tw-data-[state=closed]:tw-fade-out-0 tw-data-[state=closed]:tw-zoom-out-95 tw-data-[side=bottom]:tw-slide-in-from-top-2
          tw-data-[side=left]:tw-slide-in-from-right-2 tw-data-[side=right]:tw-slide-in-from-left-2
          tw-data-[side=top]:tw-slide-in-from-bottom-2 tw-z-50 tw-w-fit tw-origin-[var(--radix-tooltip-content-transform-origin)]
          tw-rounded-md tw-px-3 tw-py-1.5 tw-text-xs tw-text-[12px] tw-text-balance tw-max-w-[300px] tw-leading-5`,
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className={`tw-bg-foreground tw-fill-foreground tw-z-50 tw-size-2.5 tw-rotate-45 tw-rounded-[2px]
        tw-translate-y-[calc(-50%_-_2px)]`}
        />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

const TooltipContent = React.memo(TooltipContentComponent);

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
